import z from 'zod/v4';

import { IntegrationAuthType } from './integration-auth';

export type ManagedAuthHealth =
  | { status: 'ready' | 'refreshing' | 'reauth_required' }
  | { status: 'retrying' | 'configuration_error'; retryAt: string };

type AuthenticationHealthCredential = {
  issuingConnectionRevision: unknown;
  retryNotBefore: unknown;
  state: unknown;
  failure: { code: unknown } | null;
};

const connectionRevisionSchema = z.int().positive().max(2_147_483_647);
const issuingRevisionSchema = connectionRevisionSchema.nullable();
const cooldownSchema = z.object({
  retryNotBefore: z.iso
    .datetime({ offset: true })
    .refine((value) => Number.isFinite(Date.parse(value))),
  failure: z.object({
    code: z.enum([
      'configuration_error',
      'rate_limited',
      'provider_unavailable',
      'preflight_failed',
    ]),
  }),
});

export function deriveManagedAuthHealth(
  authType: IntegrationAuthType,
  connectionRevision: number,
  credential: AuthenticationHealthCredential | null,
): ManagedAuthHealth | null {
  if (authType === IntegrationAuthType.Manual || authType === IntegrationAuthType.App) return null;
  if (
    authType !== IntegrationAuthType.Oauth2Code ||
    !connectionRevisionSchema.safeParse(connectionRevision).success
  ) {
    throw new Error('Authentication status is unavailable.');
  }
  if (credential === null) return { status: 'reauth_required' };

  const issuingRevision = issuingRevisionSchema.safeParse(credential.issuingConnectionRevision);
  if (!issuingRevision.success) throw new Error('Authentication status is unavailable.');
  if (issuingRevision.data === null || issuingRevision.data !== connectionRevision) {
    return { status: 'reauth_required' };
  }

  switch (credential.state) {
    case 'ready':
      return { status: 'ready' };
    case 'claimed':
    case 'exchanging':
      return { status: 'refreshing' };
    case 'reauth_required':
      return { status: 'reauth_required' };
    case 'cooldown': {
      const cooldown = cooldownSchema.safeParse(credential);
      if (!cooldown.success) throw new Error('Authentication status is unavailable.');
      return {
        status:
          cooldown.data.failure.code === 'configuration_error' ? 'configuration_error' : 'retrying',
        retryAt: cooldown.data.retryNotBefore,
      };
    }
    default:
      throw new Error('Authentication status is unavailable.');
  }
}
